import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import styles from './FilterComponent.module.css';
import ThemeContext from 'config/context/themeContext';
import { useCompositionAdvancedOptionsContext } from '../../context/CompositionAdvancedOptionsContext';
import dataTypesConfig from '../../constants/dataTypes.json';
import {typeConverters, converterFor} from '../../utils/typeConverters';
// Constants for better maintainability
const DEBOUNCE_DELAY = 300;

// Utility functions for type conversion


const FilterComponent = ({ id, label, type, scale }) => {
    const { dark } = useContext(ThemeContext);
    const { query, setQuery } = useCompositionAdvancedOptionsContext();
    
    const [localValues, setLocalValues] = useState({ min: '', max: '' });
    const [debounceTimer, setDebounceTimer] = useState(null);

    // Konvertor po POLJU: granice filtera se kucaju kao `7978.68`, a na čvor
    // moraju otići skalirane, jer je indeks nad celim brojem.
    const converter = useMemo(() => converterFor(type, scale), [type, scale]);

    // Debounced query update
    const updateQuery = useCallback((min, max) => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        const timer = setTimeout(() => {
            const newQuery = { ...query };
            
            // Special handling for boolean type
            if (type === dataTypesConfig.supportedTypes.BOOL) {
                const hasTrue = min === 'true' && max === 'true';
                const hasFalse = min === 'false' && max === 'false';
                
                if (hasTrue) {
                    newQuery[id] = { min: true, max: true };
                } else if (hasFalse) {
                    newQuery[id] = { min: false, max: false };
                } else {
                    delete newQuery[id];
                }
            } else {
                // Convert and validate values for other types
                const convertedMin = converter.fromDisplay(min);
                const convertedMax = converter.fromDisplay(max);
                
                // Determine if we should include this filter
                const hasValidMin = converter.validate(convertedMin);
                const hasValidMax = converter.validate(convertedMax);
                
                if (hasValidMin || hasValidMax) {
                    newQuery[id] = { 
                        min: hasValidMin ? convertedMin : null, 
                        max: hasValidMax ? convertedMax : null 
                    };
                } else {
                    delete newQuery[id];
                }
            }
            
            setQuery(newQuery);
        }, DEBOUNCE_DELAY);

        setDebounceTimer(timer);
    }, [query, id, setQuery, converter, debounceTimer, type]);

    // Handle value changes
    const handleValueChange = useCallback((field, value) => {
        setLocalValues(prev => ({ ...prev, [field]: value }));
        updateQuery(
            field === 'min' ? value : localValues.min,
            field === 'max' ? value : localValues.max
        );
    }, [localValues.min, localValues.max, updateQuery]);

    // Handle boolean selection
    const handleBooleanChange = useCallback((value) => {
        const isTrue = value === true;
        const currentTrue = localValues.min === 'true';
        const currentFalse = localValues.min === 'false';
        
        if (isTrue) {
            // If True is already selected, deselect it
            if (currentTrue) {
                setLocalValues({ min: '', max: '' });
                updateQuery('', '');
            } else {
                // Select True, set both min and max to true
                setLocalValues({ min: 'true', max: 'true' });
                updateQuery('true', 'true');
            }
        } else {
            // If False is already selected, deselect it
            if (currentFalse) {
                setLocalValues({ min: '', max: '' });
                updateQuery('', '');
            } else {
                // Select False, set both min and max to false
                setLocalValues({ min: 'false', max: 'false' });
                updateQuery('false', 'false');
            }
        }
    }, [localValues.min, updateQuery]);

    // Sync with context changes
    useEffect(() => {
        const contextFilter = query[id];
        if (contextFilter) {
            if (type === dataTypesConfig.supportedTypes.BOOL) {
                setLocalValues({
                    min: contextFilter.min === true ? 'true' : contextFilter.min === false ? 'false' : '',
                    max: contextFilter.max === true ? 'true' : contextFilter.max === false ? 'false' : ''
                });
            } else {
                setLocalValues({
                    min: converter.toDisplay(contextFilter.min),
                    max: converter.toDisplay(contextFilter.max)
                });
            }
        } else {
            setLocalValues({ min: '', max: '' });
        }
    }, [query, id, converter, type]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [debounceTimer]);

    // Check if min > max (validation error)
    const hasValidationError = useMemo(() => {
        if (type === dataTypesConfig.supportedTypes.STRING || 
            type === dataTypesConfig.supportedTypes.BOOL) {
            return false;
        }

        const convertedMin = converter.fromDisplay(localValues.min);
        const convertedMax = converter.fromDisplay(localValues.max);
        
        const hasValidMin = converter.validate(convertedMin);
        const hasValidMax = converter.validate(convertedMax);
        
        if (hasValidMin && hasValidMax) {
            if (type === dataTypesConfig.supportedTypes.TIME_POINT) {
                return new Date(convertedMin) > new Date(convertedMax);
            }
            return convertedMin > convertedMax;
        }
        
        return false;
    }, [localValues.min, localValues.max, converter, type]);

    // Memoized common input props
    const commonInputProps = useMemo(() => ({
        className: `${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`,
        placeholder: (field) => field === 'min' ? 'Min' : 'Max'
    }), [dark, hasValidationError]);

    // Render different input types
    const renderInputs = () => {
        switch (type) {
            case dataTypesConfig.supportedTypes.STRING:
                return (
                    <>
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${styles.col_span_2} ${dark ? styles.dark : ''}`}
                            type="text"
                            value={localValues.min}
                            onChange={(e) => {
                                const v = e.target.value;
                                setLocalValues({ min: v, max: v });
                                updateQuery(v, v);
                            }}
                            placeholder="Value"
                        />
                    </>
                );

            case dataTypesConfig.supportedTypes.NAME:
                return (
                    <>
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="text"
                            value={localValues.min}
                            onChange={(e) => handleValueChange('min', e.target.value)}
                            placeholder={commonInputProps.placeholder('min')}
                        />
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="text"
                            value={localValues.max}
                            onChange={(e) => handleValueChange('max', e.target.value)}
                            placeholder={commonInputProps.placeholder('max')}
                        />
                    </>
                );

            case dataTypesConfig.supportedTypes.UINT128_T:
            case dataTypesConfig.supportedTypes.UINT64_T:
            case dataTypesConfig.supportedTypes.UINT32_T:
            case dataTypesConfig.supportedTypes.UINT16_T:
            case dataTypesConfig.supportedTypes.UINT8_T:
            case dataTypesConfig.supportedTypes.INT64_T:
            case dataTypesConfig.supportedTypes.INT32_T:
            case dataTypesConfig.supportedTypes.INT16_T:
            case dataTypesConfig.supportedTypes.INT8_T:
            case dataTypesConfig.supportedTypes.DOUBLE:
            case dataTypesConfig.supportedTypes.FLOAT:
                return (
                    <>
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="number"
                            value={localValues.min}
                            onChange={(e) => handleValueChange('min', e.target.value)}
                            placeholder={commonInputProps.placeholder('min')}
                        />
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="number"
                            value={localValues.max}
                            onChange={(e) => handleValueChange('max', e.target.value)}
                            placeholder={commonInputProps.placeholder('max')}
                        />
                    </>
                );

            case dataTypesConfig.supportedTypes.TIME_POINT:
                return (
                    <>
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="text"
                            value={localValues.min}
                            onChange={(e) => handleValueChange('min', e.target.value)}
                            onFocus={(e) => e.target.type = 'datetime-local'}
                            onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                            placeholder="Start date"
                        />
                        <input
                            {...commonInputProps}
                            className={`${styles.input} ${dark ? styles.dark : ''} ${hasValidationError ? styles.error : ''}`}
                            type="text"
                            value={localValues.max}
                            onChange={(e) => handleValueChange('max', e.target.value)}
                            onFocus={(e) => e.target.type = 'datetime-local'}
                            onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                            placeholder="End date"
                        />
                    </>
                );

            case dataTypesConfig.supportedTypes.BOOL:
                return (
                    <fieldset className={styles.composition_operators}>
                        <div className={styles.operator}>
                            <div className={styles.radio_thumb}>
                                <input
                                    type="checkbox"
                                    id={`bool-true-${id}`}
                                    checked={localValues.min === 'true'}
                                    onChange={() => handleBooleanChange(true)}
                                />
                            </div>
                            <label htmlFor={`bool-true-${id}`}>True</label>
                        </div>

                        <div className={styles.operator}>
                            <div className={styles.radio_thumb}>
                                <input
                                    type="checkbox"
                                    id={`bool-false-${id}`}
                                    checked={localValues.min === 'false'}
                                    onChange={() => handleBooleanChange(false)}
                                />
                            </div>
                            <label htmlFor={`bool-false-${id}`}>False</label>
                        </div>
                    </fieldset>
                );

            default:
                return null;
        }
    };

    if (!typeConverters[type]) {
        return null;
    }

    return (
        <div className={`${styles.filter_group} ${dark ? styles.dark : ''}`}>
            {label && <div className={`${styles.label} ${hasValidationError ? styles.error : ''}`}>{label}</div>}
            <div className={styles.filter_row}>
                {renderInputs()}
            </div>
        </div>
    );
};

export default React.memo(FilterComponent);